/**
 * Lightweight checklist-only settlement screen with frame persistence via useSettlementSession.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { useSettlement } from '../../contexts/SettlementContext';
import { persistPayDebtsHubSectionAndSyncDt3, type PayDebtsHubKey } from '../sframes/debts/payDebtsHub';
import { syncDt4GuidedFromChildren } from '../sframes/debts/taxFilingsGuided';

export type SettlementChecklistItemDef = {
  id: string;
  title: string;
  description: string;
};

type Props = {
  estateId: string;
  frameId: string;
  title: string;
  /** e.g. "/sframe-204/xyz" — full path segment after origin */
  backTo: string;
  backLabel: string;
  items: SettlementChecklistItemDef[];
  /** When hub subsection completes (all boxes checked), mark this pay-debts hub key */
  payDebtsHubKey?: PayDebtsHubKey;
  /** When true, reconcile debts-taxes step dt-4 (Identify Tax Filings) from guided tax checklist frames after each save */
  syncTaxDt4Guided?: boolean;
};

export default function SettlementChecklistShell(props: Props) {
  const {
    estateId,
    frameId,
    title,
    backTo,
    backLabel,
    items,
    payDebtsHubKey,
    syncTaxDt4Guided,
  } = props;
  const navigate = useNavigate();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, updateStepProgress, error } = useSettlementSession(estateId, {
    readOnly: isArchived,
  });

  const itemIds = useMemo(() => items.map((i) => i.id).join('|'), [items]);

  const [checks, setChecks] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(items.map((i) => [i.id, false]))
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const framed = await loadFrameData(frameId);
      const rawChecks = framed?.data?.checks as Record<string, boolean> | undefined;
      const next = Object.fromEntries(items.map(({ id }) => [id, !!(rawChecks && rawChecks[id])]));
      if (!cancelled) setChecks(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, frameId, loadFrameData, itemIds, items]);

  const persist = useCallback(
    async (next: Record<string, boolean>) => {
      if (!estateId || isArchived) return;
      const framed = await loadFrameData(frameId);
      const prev =
        typeof framed?.data === 'object' && framed?.data ? (framed.data as Record<string, unknown>) : {};
      await saveFrameData(frameId, { ...prev, checks: next });
      setChecks(next);

      if (payDebtsHubKey) {
        const nextAllDone = items.every(({ id }) => next[id]);
        await persistPayDebtsHubSectionAndSyncDt3(
          loadFrameData,
          saveFrameData,
          updateStepProgress,
          payDebtsHubKey,
          nextAllDone
        );
      }
      if (syncTaxDt4Guided) {
        const guidedSectionsDone = await syncDt4GuidedFromChildren(loadFrameData, updateStepProgress);
        const hubFd = await loadFrameData('sframe-207');
        const hubPrev =
          typeof hubFd?.data === 'object' && hubFd?.data ? (hubFd.data as Record<string, unknown>) : {};
        await saveFrameData('sframe-207', {
          ...hubPrev,
          guidedTaxSectionsCompleted: guidedSectionsDone,
        });
      }
    },
    [
      estateId,
      frameId,
      isArchived,
      items,
      loadFrameData,
      payDebtsHubKey,
      saveFrameData,
      syncTaxDt4Guided,
      updateStepProgress,
    ]
  );

  const toggle = (itemId: string) => {
    if (isArchived) return;
    const next = { ...checks, [itemId]: !checks[itemId] };
    void persist(next);
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {error ? (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      ) : null}
      <button
        type="button"
        onClick={() => navigate(backTo)}
        className="flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-6 text-sm"
      >
        <ChevronLeft className="w-4 h-4" /> {backLabel}
      </button>
      <h1 className="text-2xl font-semibold text-stone-800 mb-6">{title}</h1>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="bg-white rounded-xl border border-stone-200 p-5 flex items-start gap-4">
            <input
              type="checkbox"
              checked={!!checks[item.id]}
              onChange={() => toggle(item.id)}
              disabled={isArchived}
              className="mt-1 h-5 w-5 rounded border-stone-300 text-cyan-600 cursor-pointer disabled:opacity-50"
            />
            <div className="flex-1">
              <p className="font-medium text-stone-800">{item.title}</p>
              <p className="text-stone-500 text-sm mt-1">{item.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
